/**
 * Publish Service — frontend client for the Publish API
 *
 * Owner-management calls (require Supabase JWT) and visitor-facing calls
 * (require a publish session token) live in the same file but use different
 * auth headers.
 *
 * Owner endpoints: /api/v1/publishes/...
 * Visitor endpoints: /api/v1/p/:slug/...
 */

import { supabase } from '../lib/supabase';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : '');

export interface PublishRow {
  id: string;
  flow_id: string;
  user_id: string;
  slug: string;
  title: string | null;
  description: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface PublishMetadata {
  slug: string;
  requiresLogin: boolean;
  title: string | null;
  description: string | null;
}

export interface InputSchemaEntry {
  nodeId: string;
  label: string;
  type: 'text' | 'image' | 'video' | 'audio';
  required: boolean;
}

export interface OutputSchemaEntry {
  nodeId: string;
  label: string;
  type: string;
}

export interface UnlockResponse {
  sessionToken: string;
  expiresAt: string;
  title: string | null;
  description: string | null;
  inputs: InputSchemaEntry[];
  outputs: OutputSchemaEntry[];
  /** Estimated credit cost of one full run of this flow. UX hint, not a charge. */
  estimatedCostPerRun: number;
  /** Owner's current credit balance at unlock time. May be null if lookup failed. */
  ownerBalance: number | null;
}

export interface RunStartResponse {
  runId: string;
  status: string;
  pollUrl: string;
}

export interface RunStatus {
  runId: string;
  flowId: string;
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled';
  progress: { completed: number; total: number };
  outputs: Record<string, any> | null;
  nodeStates: Record<string, any>;
  totalCreditsUsed: number;
  error?: { message: string; nodeId?: string };
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not signed in');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
  };
}

async function jsonOrThrow(res: Response): Promise<any> {
  const text = await res.text();
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    throw new Error(body?.error || `Request failed: ${res.status}`);
  }
  return body;
}

// ===========================================================================
// OWNER MANAGEMENT (requires Supabase JWT)
// ===========================================================================

export interface PublishListItem {
  id: string;
  flowId: string;
  slug: string;
  title: string | null;
  description: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  flowName: string | null;
  flowDescription: string | null;
  thumbnailUrl: string | null;
}

/**
 * List every publish owned by the caller (joined with the source flow).
 */
export async function listPublishes(): Promise<PublishListItem[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/v1/publishes`, { headers });
  return jsonOrThrow(res);
}

/**
 * Fetch the publish row for a flow, or null if it isn't published yet.
 */
export async function getPublishByFlow(flowId: string): Promise<PublishRow | null> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/v1/publishes/by-flow/${flowId}`, { headers });
  if (res.status === 404) return null;
  return jsonOrThrow(res);
}

export async function createPublish(args: {
  flowId: string;
  title?: string;
  description?: string;
}): Promise<PublishRow> {
  const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const res = await fetch(`${API_BASE}/api/v1/publishes`, {
    method: 'POST',
    headers,
    body: JSON.stringify(args),
  });
  return jsonOrThrow(res);
}

export async function updatePublish(
  id: string,
  updates: {
    title?: string | null;
    description?: string | null;
    enabled?: boolean;
  },
): Promise<PublishRow> {
  const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const res = await fetch(`${API_BASE}/api/v1/publishes/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  return jsonOrThrow(res);
}

export async function deletePublish(id: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/v1/publishes/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok && res.status !== 204) {
    await jsonOrThrow(res);
  }
}

/**
 * Build the public-facing URL for a published Knod.
 */
export function buildPublishUrl(slug: string): string {
  if (typeof window === 'undefined') return `/p/${slug}`;
  return `${window.location.origin}/p/${slug}`;
}

// ===========================================================================
// VISITOR-FACING (no JWT — uses publish session token after unlock)
// ===========================================================================

export async function getPublishMetadata(slug: string): Promise<PublishMetadata> {
  const res = await fetch(`${API_BASE}/api/v1/p/${slug}`);
  return jsonOrThrow(res);
}

/**
 * Unlock a publish view by passing the visitor's Supabase JWT. Server checks
 * that the JWT user is the owner. Returns a short-lived session token used
 * for subsequent calls.
 */
export async function unlockPublish(
  slug: string,
  accountToken: string,
): Promise<UnlockResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accountToken}`,
  };
  const res = await fetch(`${API_BASE}/api/v1/p/${slug}/unlock`, {
    method: 'POST',
    headers,
    body: '{}',
  });
  return jsonOrThrow(res);
}

export async function uploadPublishFile(
  slug: string,
  sessionToken: string,
  file: File,
): Promise<{ url: string; mimeType: string; size: number }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/api/v1/p/${slug}/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}` },
    body: formData,
  });
  return jsonOrThrow(res);
}

export async function startPublishRun(
  slug: string,
  sessionToken: string,
  inputs: Array<{ nodeId: string; content: string; type: string }>,
  options?: { targetOutputKey?: string },
): Promise<RunStartResponse> {
  const body: Record<string, unknown> = { inputs };
  if (options?.targetOutputKey) body.targetOutputKey = options.targetOutputKey;
  const res = await fetch(`${API_BASE}/api/v1/p/${slug}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function getPublishRunStatus(
  slug: string,
  sessionToken: string,
  runId: string,
): Promise<RunStatus> {
  const res = await fetch(`${API_BASE}/api/v1/p/${slug}/runs/${runId}`, {
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  return jsonOrThrow(res);
}
